import type {
  BaseServiceOptions,
  Pagination,
  TFilters,
  TRecordAction,
} from '@/core/types'
import {
  type TInvitation,
  moduleServiceHandlers,
  type TInvitations,
  serviceConfig,
  catalogServiceHandlers,
  TSeller,
  TLead,
  TStatuses,
  TStates,
  TCities,
  TEmail,
  moduleExportHandler,
} from '../../..'
import {
  defaultPagination,
  useNotification,
  useServiceInfiniteQueryHandlers,
} from '@/core'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { useInvitationStore } from '../../store/invitation'

const { model } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { handlers, enableQuery, filters } = options

  const { errorHttpNotification, successNotification } = useNotification()
  const store = useInvitationStore()
  const serviceHandlers = useServiceInfiniteQueryHandlers<TInvitation>({
    model,
    filterParams: filters?.loadRecords ?? '',
  })

  const loadRecords = useInfiniteQuery<TInvitations, AxiosError>({
    queryKey: [model, filters?.loadRecords],
    queryFn: ({ pageParam }) =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => (lastPage?.meta?.current_page ?? 0) + 1,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      const lastPage = [...(data?.pages ?? [])].pop()

      if (lastPage) {
        const pages = data.pages.flatMap((page) => page.data)

        handlers?.onDataChange?.(lastPage.meta ?? defaultPagination)

        store.setInvitations(pages)
      }
    },
    enabled: enableQuery?.loadRecords,
    refetchOnWindowFocus: false,
  })

  const sendQuotationEmail = useMutation<string, AxiosError, TEmail>(
    (payload: TEmail) => {
      const emailFormData = new FormData()
      emailFormData.append('invitation_id', payload.invitation_id.toString())
      emailFormData.append('subject', payload.subject)
      emailFormData.append('body', payload.body)

      payload.attachments.forEach((attach) => {
        emailFormData.append('attachments[]', attach)
      })

      payload.emails.forEach((email) => {
        emailFormData.append('emails[]', email)
      })

      return moduleServiceHandlers.sendQuotationEmail(emailFormData)
    },
    {
      onSuccess: (_, payload) => {
        successNotification({
          detail: `Correo Enviado exitosamente a ${payload.emails.join(',')}`,
          summary: 'Correo Enviado',
        })
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TInvitation, AxiosError, TInvitation>(
    (payload: TInvitation) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => {
        serviceHandlers.addRecord(payload)
        store.addInvitation(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TInvitation, AxiosError, TRecordAction>(
    (payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecord = useMutation<void, AxiosError, TInvitation>(
    (payload) => moduleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, payload) => {
        serviceHandlers.deleteRecord(payload)
        store.deleteInvitation(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecords = useMutation<void, AxiosError, Array<number>>(
    (payload) => moduleServiceHandlers.deleteRecords({ ids: payload }),
    {
      onSuccess: (_, ids) => serviceHandlers.deleteRecords(ids),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TInvitation, AxiosError, TInvitation>(
    (payload: TInvitation) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => {
        serviceHandlers.updateRecord(payload)
        store.updateInvitation(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const sellersCatalog = useQuery<TSeller[]>(
    [serviceConfig.sellers],
    () => catalogServiceHandlers.sellersCatalog.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.sellersCatalog,
    },
  )

  const statusCatalog = useQuery<TStatuses>(
    [serviceConfig.status, filters?.statusCatalog],
    () =>
      catalogServiceHandlers.statusCatalog.loadCatalog({
        dynamicFilters: filters?.statusCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.statusCatalog,
    },
  )

  const leadsCatalog = useQuery<TLead[]>(
    [serviceConfig.leads, filters?.leadsCatalog],
    () =>
      catalogServiceHandlers.leadsCatalog.loadCatalog({
        dynamicFilters: filters?.leadsCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.leadsCatalog,
    },
  )

  const statesCatalog = useQuery<TStates>(
    [serviceConfig.states],
    () => catalogServiceHandlers.statesCatalog.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.statesCatalog,
    },
  )

  const citiesCatalog = useQuery<TCities>(
    [serviceConfig.cities, filters?.citiesCatalog],
    () =>
      catalogServiceHandlers.citiesCatalog.loadCatalog({
        dynamicFilters: filters?.citiesCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.citiesCatalog,
    },
  )

  const exportRecords = useMutation<void, AxiosError, TFilters>(
    (payload: TFilters) => moduleExportHandler.exportRecords(payload, 'pdf'),
    {
      onError: (error) => errorHttpNotification(error),
    },
  )

  return {
    exportRecords,
    loadRecords,
    addRecord,
    editRecord,
    deleteRecords,
    deleteRecord,
    actionRecord,
    sellersCatalog,
    statusCatalog,
    leadsCatalog,
    statesCatalog,
    citiesCatalog,
    sendQuotationEmail,
  }
}
